/**
 * Gallery Upload Service
 * Client-side utilities for uploading photos to the gallery system
 */

import { getAuth } from 'firebase/auth';

/**
 * Date detection result from server
 */
export interface DateDetectionResult {
  filename: string;
  detectedDate: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  suggestedAlbum: string | null;
  shouldPrompt: boolean;
}

/**
 * Upload response from server
 */
export interface GalleryUploadResponse {
  success: boolean;
  uploadedMedia: Array<{
    id: string;
    filename: string;
    url: string;
    thumbnailUrl: string;
    width: number;
    height: number;
  }>;
  errors?: Array<{
    filename: string;
    error: string;
  }>;
  dateDetectionResults?: DateDetectionResult[];
  duplicatesSkipped?: number;
  duplicatesOverwritten?: number;
}

/**
 * Upload options
 */
export interface GalleryUploadOptions {
  albumId: string;
  title?: string;
  description?: string;
  tags?: string[];
  visibility?: 'public' | 'members' | 'officers' | 'private';
  useExifDate?: boolean;
  duplicateStrategy?: 'keep-original' | 'overwrite' | 'keep-both';
  onProgress?: (progress: number, filename: string) => void;
}

/**
 * Upload photos to gallery (requires authentication)
 * @param files - Array of image files to upload
 * @param options - Upload options including albumId and metadata
 * @returns Upload response with results and any errors
 */
export async function uploadGalleryPhotos(
  files: File[],
  options: GalleryUploadOptions
): Promise<GalleryUploadResponse> {
  try {
    // Validate files
    if (!files || files.length === 0) {
      throw new Error('No files provided');
    }

    // Validate album ID
    if (!options.albumId) {
      throw new Error('Album ID is required');
    }

    // Validate file types and sizes
    const maxSize = 50 * 1024 * 1024; // 50MB
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        throw new Error(`File "${file.name}" is not an image`);
      }
      if (file.size > maxSize) {
        throw new Error(`File "${file.name}" exceeds 50MB limit`);
      }
    }

    // Get the current user's auth token
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('You must be logged in to upload photos');
    }

    const token = await user.getIdToken();

    // Create form data
    const formData = new FormData();

    // Add all files
    files.forEach(file => formData.append('files', file));

    // Add options
    formData.append('albumId', options.albumId);
    if (options.title) formData.append('title', options.title);
    if (options.description) formData.append('description', options.description);
    if (options.tags && options.tags.length > 0) {
      formData.append('tags', options.tags.join(','));
    }
    if (options.visibility) formData.append('visibility', options.visibility);
    if (options.useExifDate !== undefined) {
      formData.append('useExifDate', options.useExifDate.toString());
    }
    if (options.duplicateStrategy) {
      formData.append('duplicateStrategy', options.duplicateStrategy);
    }

    // Upload with progress tracking
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && typeof options.onProgress === 'function') {
          const progress = Math.round((e.loaded / e.total) * 100);
          options.onProgress(progress, 'Uploading...');
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response: GalleryUploadResponse = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            console.error('Failed to parse upload response:', xhr.responseText);
            reject(new Error('Failed to parse server response'));
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject(new Error(error.error || `Upload failed with status ${xhr.status}`));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      // Send request with auth token
      // Use production upload URL if UPLOADS_BASE_URL is set in environment
      const uploadUrl = process.env.NEXT_PUBLIC_UPLOADS_BASE_URL
        ? `${process.env.NEXT_PUBLIC_UPLOADS_BASE_URL}/api/gallery/upload`
        : '/api/gallery/upload';

      xhr.open('POST', uploadUrl);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  } catch (error) {
    console.error('Gallery upload error:', error);
    throw error;
  }
}

/**
 * Upload a single photo to gallery
 * Convenience wrapper around uploadGalleryPhotos
 */
export async function uploadGalleryPhoto(
  file: File,
  options: GalleryUploadOptions
): Promise<GalleryUploadResponse> {
  return uploadGalleryPhotos([file], options);
}

/**
 * Check if any uploaded photos have date detection results
 */
export function hasDateDetectionResults(
  response: GalleryUploadResponse
): boolean {
  return !!(response.dateDetectionResults && response.dateDetectionResults.length > 0);
}

/**
 * Get photos that should prompt for Field Flights placement
 */
export function getPhotosForFieldFlightsPrompt(
  response: GalleryUploadResponse
): DateDetectionResult[] {
  if (!response.dateDetectionResults) return [];
  return response.dateDetectionResults.filter(r => r.shouldPrompt);
}

/**
 * Get unique suggested albums from date detection results
 */
export function getUniqueSuggestedAlbums(
  response: GalleryUploadResponse
): string[] {
  if (!response.dateDetectionResults) return [];

  const albums = response.dateDetectionResults
    .map(r => r.suggestedAlbum)
    .filter((album): album is string => album !== null);

  return Array.from(new Set(albums));
}

/**
 * Check if all photos are from the same date
 */
export function areAllPhotosFromSameDate(
  response: GalleryUploadResponse
): boolean {
  if (!response.dateDetectionResults || response.dateDetectionResults.length === 0) {
    return false;
  }

  const dates = response.dateDetectionResults
    .map(r => r.detectedDate)
    .filter((date): date is string => date !== null);

  if (dates.length === 0) return false;

  const uniqueDates = new Set(dates);
  return uniqueDates.size === 1;
}

/**
 * Get the common date from all photos (if they're all from the same date)
 */
export function getCommonDate(
  response: GalleryUploadResponse
): string | null {
  if (!areAllPhotosFromSameDate(response)) return null;

  const firstResult = response.dateDetectionResults?.[0];
  return firstResult?.detectedDate || null;
}

/**
 * Format date detection confidence for display
 */
export function formatConfidence(
  confidence: DateDetectionResult['confidence']
): string {
  switch (confidence) {
    case 'high':
      return 'High confidence (EXIF data)';
    case 'medium':
      return 'Medium confidence';
    case 'low':
      return 'Low confidence';
    case 'none':
      return 'No date detected';
    default:
      return 'Unknown';
  }
}

/**
 * Validate image file before upload
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check if it's an image
  if (!file.type.startsWith('image/')) {
    return {
      valid: false,
      error: 'File must be an image',
    };
  }

  // Check file size (50MB max)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size (${sizeMB}MB) exceeds 50MB limit`,
    };
  }

  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} not supported. Use JPEG, PNG, GIF, or WebP`,
    };
  }

  return { valid: true };
}

/**
 * Validate multiple image files
 */
export function validateImageFiles(files: File[]): {
  valid: boolean;
  errors: Array<{ filename: string; error: string }>;
} {
  const errors: Array<{ filename: string; error: string }> = [];

  for (const file of files) {
    const result = validateImageFile(file);
    if (!result.valid && result.error) {
      errors.push({
        filename: file.name,
        error: result.error,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Convert File to data URL for preview
 */
export function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
